import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import Button from '@components/common/Button';
import SectionHeader from '@components/common/SectionHeader';
import { COMPANY_INFO, MISSION } from '@utils/constants';

/**
 * AboutPreview Component
 * Homepage section introducing the company
 */
const AboutPreview = () => {
  // Key features/highlights
  const highlights = [
    'Over 1,052 individual farmer members',
    '10 primary cooperative societies',
    'UNBS & GMP certified products',
    'Established in 2003, registered 2004',
    'Ethical sourcing and traceability',
    'Post-harvest handling expertise',
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Images */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-large">
              <img
                src="/ooopHQ.jpeg"
                alt="Bukawa ACE Ltd Headquarters"
                className="w-full h-[500px] object-cover"
              />
              {/* Overlay Badge */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white rounded-xl p-6 shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-16 h-16 bg-bukawa-primary rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">
                        {new Date().getFullYear() - COMPANY_INFO.yearEstablished}+
                      </span>
                    </div>
                    <div>
                      <div className="text-sm text-bukawa-gray-600">
                        Years of Excellence
                      </div>
                      <div className="text-lg font-semibold text-bukawa-primary">
                        Serving Since {COMPANY_INFO.yearEstablished}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Image - Floating */}
            <div className="hidden lg:block absolute -top-8 -right-8 w-48 h-48 rounded-2xl overflow-hidden shadow-large border-8 border-white">
              <img
                src="/Bukawalogo.jpeg"
                alt="Bukawa Logo"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Decorative Element */}
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-bukawa-secondary rounded-full opacity-20 blur-3xl" />
          </div>

          {/* Right Column - Content */}
          <div>
            <SectionHeader
              subtitle="About Us"
              title="Empowering Farmers Through Collective Marketing"
              centered={false}
              className="mb-6"
            />

            <p className="text-lg text-bukawa-gray-700 mb-6 leading-relaxed">
              {COMPANY_INFO.name} is a secondary cooperative organization 
              established in {COMPANY_INFO.yearEstablished} and officially registered 
              on {COMPANY_INFO.registrationDate} with Registration Number {COMPANY_INFO.registrationNumber}.
            </p>

            <p className="text-base text-bukawa-gray-600 mb-8 leading-relaxed">
              {MISSION}
            </p>

            {/* Highlights Grid */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {highlights.map((highlight, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3"
                >
                  <FiCheckCircle className="h-5 w-5 text-bukawa-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-bukawa-gray-700">
                    {highlight}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                to="/about"
                variant="primary"
                size="lg"
                icon={<FiArrowRight />}
                iconPosition="right"
              >
                Learn More About Us
              </Button>
              <Button
                to="/membership"
                variant="outline"
                size="lg"
              >
                Become a Member
              </Button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-bukawa-gray-200">
              <div>
                <div className="text-3xl font-bold text-bukawa-primary mb-1">
                  {COMPANY_INFO.totalMembers}+
                </div>
                <div className="text-sm text-bukawa-gray-600">
                  Members
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-bukawa-primary mb-1">
                  {COMPANY_INFO.totalCooperatives}
                </div>
                <div className="text-sm text-bukawa-gray-600">
                  Cooperatives
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-bukawa-primary mb-1">
                  100%
                </div>
                <div className="text-sm text-bukawa-gray-600">
                  Quality
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;